import { objectSpread2 as _objectSpread2, asyncToGenerator as _asyncToGenerator, defineProperty as _defineProperty } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { stringify } from '../../node_modules/query-string/index.js';
import { getApiModuleOptions } from './config.js';

var NAMESPACE = "API";
var API_REQUEST = "".concat(NAMESPACE, "/REQUEST");
var ApiRequestMethod = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE"
};
/**
 * Generic action creator for API requests
 * (use apiGet, apiPost etc, common methods below for most requests, use apiRequest for non-standard requests)
 * @param {Object} options Specification for the API request
 * @param {string} options.type Redux action type
 * @param {string} options.method Request method type, e.g. GET, POST, PUT, DELETE
 * @param {string} options.path Combines the base project API URL with this route for the API endpoint
 * @param {string} options.url Use this instead of path to override the full URL for the API endpoint
 * @param {Object} options.params Optional, query string parameters to append to the request URL
 * @param {string} options.contentType Optional, the content type for the request, defaults to 'application/json'
 * @param {Object} options.headers Optional, object containing key-value pairs for custom HTTP headers
 * @param {*} options.body Optional, the body of the request
 * @param {boolean} options.auth Optional, whether the request should be authenticated (using the auth module/JWT), defaults to true. Can be supplied a string to use a custom authentication method / Authorization header
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number} options.actionSlowDelay Optional, time delay (ms) for an additional action to be dispatched to indicate the request is taking longer than expected, default 200ms
 * @param {boolean} options.preventErrorNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {boolean} options.preventResponseHandling Optional, prevents the default response handling, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */

var apiRequest = function apiRequest(options) {
  var url = options.path && options.path !== undefined ? getApiModuleOptions().apiUrl + options.path : options.url || "";
  var urlWithParams = options.params && !!Object.keys(options.params).length ? "".concat(url).concat(url.indexOf("?") === -1 ? "?" : "&").concat(stringify(options.params)) : url;
  return {
    type: API_REQUEST,
    requestType: options.type,
    method: options.method,
    url: urlWithParams,
    contentType: options.contentType !== undefined ? options.contentType : "application/json",
    headers: options.headers,
    body: options.body !== undefined ? options.body : null,
    auth: options.auth !== undefined ? options.auth : true,
    actionCustomData: options.actionCustomData,
    actionSlowDelay: options.actionSlowDelay !== undefined ? options.actionSlowDelay : 200,
    preventErrorNotification: options.preventErrorNotification || false,
    preventResponseHandling: options.preventResponseHandling || false,
    debounce: typeof options.debounce === "number" ? options.debounce : false,
    abort: options.abort
  };
};

var ApiRequestsErrorPriority = {
  First: "first",
  Last: "last",
  Earliest: "earliest",
  Latest: "latest"
};
/**
 * Action creator for combining multiple API requests
 * @param {Object|Array} apiActions One or more API actions to be requested. Can be a keyed-object (the final response will be an object with matching keyed responses), or an array (the final response will be an array of responses)
 * @param {Object} options Additional options for handling the requests
 * @param {string} options.errorPriority Optional, give priority to errors for specific requests, can be one of "first", "last", "earliest", "latest" (default is earliest)
 * @returns {function} Redux action thunk
 */

var apiRequests = function apiRequests(apiActions) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(dispatch) {
      var mapResponses, mapErrors, mappedActions, keys, requests, results, errorResults, response, error;
      return regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              mapResponses = function mapResponses(r) {
                return r;
              };

              mapErrors = function mapErrors(e) {
                switch (options.errorPriority) {
                  case ApiRequestsErrorPriority.First:
                    return e[0].error;

                  case ApiRequestsErrorPriority.Last:
                    return e[e.length - 1].error;

                  case ApiRequestsErrorPriority.Earliest:
                  default:
                    return e.reduce(function (err, next) {
                      return !err || next.finish < err.finish ? next : err;
                    }).error;

                  case ApiRequestsErrorPriority.Latest:
                    return e.reduce(function (err, next) {
                      return !err || next.finish > err.finish ? next : err;
                    }).error;
                }
              };

              mappedActions = [];

              if (Array.isArray(apiActions)) {
                mappedActions = apiActions;

                mapResponses = function mapResponses(r) {
                  return r.map(function (x) {
                    return x.response;
                  });
                };
              } else {
                keys = Object.keys(apiActions);
                mappedActions = keys.map(function (k) {
                  return apiActions[k];
                });

                mapResponses = function mapResponses(r) {
                  return r.reduce(function (acc, val, i) {
                    return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, keys[i], val.response));
                  }, {});
                };
              }

              requests = mappedActions.map( /*#__PURE__*/function () {
                var _ref2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(a) {
                  var r;
                  return regeneratorRuntime.wrap(function _callee$(_context) {
                    while (1) {
                      switch (_context.prev = _context.next) {
                        case 0:
                          _context.next = 2;
                          return dispatch(a);

                        case 2:
                          r = _context.sent;
                          return _context.abrupt("return", _objectSpread2(_objectSpread2({}, r), {}, {
                            finish: Date.now()
                          }));

                        case 4:
                        case "end":
                          return _context.stop();
                      }
                    }
                  }, _callee);
                }));

                return function (_x2) {
                  return _ref2.apply(this, arguments);
                };
              }());
              _context2.next = 7;
              return Promise.all(requests);

            case 7:
              results = _context2.sent;
              errorResults = results.filter(function (x) {
                return !!x.error;
              });

              if (!(errorResults.length === 0)) {
                _context2.next = 12;
                break;
              }

              response = mapResponses(results);
              return _context2.abrupt("return", {
                success: true,
                response: response
              });

            case 12:
              error = mapErrors(errorResults);
              return _context2.abrupt("return", {
                success: false,
                error: error
              });

            case 14:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }();
};
/**
 * Action creator for HTTP "GET" requests
 * @param {string} type Redux action type
 * @param {string} path The route for the API endpoint (combined with the base project API URL)
 * @param {Object} params Optional, query string parameters to append to the request URL
 * @param {Object} options Optional, additional specification for the API request
 * @param {string} options.contentType Optional, the content type for the request, defaults to 'application/json'
 * @param {Object} options.headers Optional, object containing key-value pairs for custom HTTP headers
 * @param {boolean} options.auth Optional, whether the request should be authenticated (using the auth module/JWT), defaults to true. Can be supplied a string to use a custom authentication method / Authorization header
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number} options.actionSlowDelay Optional, time delay (ms) for an additional action to be dispatched to indicate the request is taking longer than expected, default 200ms
 * @param {boolean} options.preventErrorNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {boolean} options.preventResponseHandling Optional, prevents the default response handling, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */


var apiGet = function apiGet(type, path) {
  var params = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return apiRequest(_objectSpread2(_objectSpread2({}, options), {}, {
    type: type,
    method: ApiRequestMethod.GET,
    path: path,
    params: params
  }));
};
/**
 * Action creator for HTTP "POST" requests
 * @param {string} type Redux action type
 * @param {string} path The route for the API endpoint (combined with the base project API URL)
 * @param {*} body Optional, the body of the request
 * @param {Object} options Optional, additional specification for the API request
 * @param {string} options.contentType Optional, the content type for the request, defaults to 'application/json'
 * @param {Object} options.headers Optional, object containing key-value pairs for custom HTTP headers
 * @param {boolean} options.auth Optional, whether the request should be authenticated (using the auth module/JWT), defaults to true. Can be supplied a string to use a custom authentication method / Authorization header
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number} options.actionSlowDelay Optional, time delay (ms) for an additional action to be dispatched to indicate the request is taking longer than expected, default 200ms
 * @param {boolean} options.preventErrorNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {boolean} options.preventResponseHandling Optional, prevents the default response handling, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */


var apiPost = function apiPost(type, path) {
  var body = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return apiRequest(_objectSpread2(_objectSpread2({}, options), {}, {
    type: type,
    method: ApiRequestMethod.POST,
    path: path,
    body: body
  }));
};
/**
 * Action creator for HTTP "PUT" requests
 * @param {string} type Redux action type
 * @param {string} path The route for the API endpoint (combined with the base project API URL)
 * @param {*} body Optional, the body of the request
 * @param {Object} options Optional, additional specification for the API request
 * @param {string} options.contentType Optional, the content type for the request, defaults to 'application/json'
 * @param {Object} options.headers Optional, object containing key-value pairs for custom HTTP headers
 * @param {boolean} options.auth Optional, whether the request should be authenticated (using the auth module/JWT), defaults to true. Can be supplied a string to use a custom authentication method / Authorization header
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number} options.actionSlowDelay Optional, time delay (ms) for an additional action to be dispatched to indicate the request is taking longer than expected, default 200ms
 * @param {boolean} options.preventErrorNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {boolean} options.preventResponseHandling Optional, prevents the default response handling, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */


var apiPut = function apiPut(type, path) {
  var body = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return apiRequest(_objectSpread2(_objectSpread2({}, options), {}, {
    type: type,
    method: ApiRequestMethod.PUT,
    path: path,
    body: body
  }));
};
/**
 * Action creator for HTTP "PATCH" requests
 * @param {string} type Redux action type
 * @param {string} path The route for the API endpoint (combined with the base project API URL)
 * @param {*} body Optional, the body of the request
 * @param {Object} options Optional, additional specification for the API request
 * @param {string} options.contentType Optional, the content type for the request, defaults to 'application/json'
 * @param {Object} options.headers Optional, object containing key-value pairs for custom HTTP headers
 * @param {boolean} options.auth Optional, whether the request should be authenticated (using the auth module/JWT), defaults to true. Can be supplied a string to use a custom authentication method / Authorization header
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number} options.actionSlowDelay Optional, time delay (ms) for an additional action to be dispatched to indicate the request is taking longer than expected, default 200ms
 * @param {boolean} options.preventErrorNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {boolean} options.preventResponseHandling Optional, prevents the default response handling, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */


var apiPatch = function apiPatch(type, path) {
  var body = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return apiRequest(_objectSpread2(_objectSpread2({}, options), {}, {
    type: type,
    method: ApiRequestMethod.PATCH,
    path: path,
    body: body
  }));
};
/**
 * Action creator for HTTP "DELETE" requests
 * @param {string} type Redux action type
 * @param {string} path The route for the API endpoint (combined with the base project API URL)
 * @param {*} body Optional, the body of the request
 * @param {Object} options Optional, additional specification for the API request
 * @param {string} options.contentType Optional, the content type for the request, defaults to 'application/json'
 * @param {Object} options.headers Optional, object containing key-value pairs for custom HTTP headers
 * @param {boolean} options.auth Optional, whether the request should be authenticated (using the auth module/JWT), defaults to true. Can be supplied a string to use a custom authentication method / Authorization header
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number} options.actionSlowDelay Optional, time delay (ms) for an additional action to be dispatched to indicate the request is taking longer than expected, default 200ms
 * @param {boolean} options.preventErrorNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {boolean} options.preventResponseHandling Optional, prevents the default response handling, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */


var apiDelete = function apiDelete(type, path) {
  var body = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return apiRequest(_objectSpread2(_objectSpread2({}, options), {}, {
    type: type,
    method: ApiRequestMethod.DELETE,
    path: path,
    body: body
  }));
};

export { API_REQUEST, ApiRequestMethod, ApiRequestsErrorPriority, apiDelete, apiGet, apiPatch, apiPost, apiPut, apiRequest, apiRequests };
